import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../../../../services/axios";
import Constants from "../../../../../constants/Index";
import { ICategory } from "../../../../../@types/app/ICategory";
import { ICoords } from "../../../../../@types/GoogleMaps/ICoord";
import { useEditOnboarding } from "../../../../../hooks/useEditOnboarding";
import { AlertModal } from "../../../../Modal/Alert/Index";
import { ButtonRedirect } from "../../../../Modal/Error/ButtonRedirect/Index";
import { TitleModal } from "../../../../Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../../Modal/Contents/TextModal/Index";
import { SimpleButtonClose } from "../../../../Modal/Contents/SimpleButtonClose/Index";
import { FlyToBounds, FlyToBoundsRefProps } from "../../../../Leaflet/FlyToBounds/Index";
import { LeafletStaticMap } from "../../../../Leaflet/LeafletStaticMap/Index";
import { ReactComponent as Animais } from "../../../../../assets/svg/icons/animais.svg";
import { Polygon } from "react-leaflet";
import { FooterEditOnboarding } from "../../../../EditOnboarding/Footer";
import {
  BatchFormV2,
  BatchFormField,
  BatchFormValueV2,
  FieldErrorsV2,
  OptionV2,
} from "../../../../FormsV2";
import "./styles.css";

function createEmptyBatchForm(): BatchFormValueV2 {
  return {
    farm: null,
    area: null,
    initials: "",
    categories: [],
    total_ua: "0.00",
    current_micro_area: null,
    micro_area_route_ids: [],
  };
}

function normalizeCoords(coords?: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords;
  if (Array.isArray(coords?.coords)) return coords.coords;
  return [];
}

function toOption(value: any): OptionV2 | null {
  if (!value) return null;
  if (typeof value === "object" && "label" in value && "value" in value) return value as OptionV2;
  return null;
}

function calculateTotalUa(categories: BatchFormValueV2["categories"]) {
  const total = categories.reduce((sum, category) => {
    return sum + (Number(category.quantity || 0) * Number(category.middle_weight || 0)) / 450;
  }, 0);
  return total.toFixed(2);
}

function getOptionByValue(options: OptionV2[] = [], value: any) {
  return options.find((option) => String(option.value) === String(value)) || null;
}

function getCategoryMiddleWeight(category?: ICategory) {
  const middleWeight = category?.middle_weight;
  if (Array.isArray(middleWeight)) return Number(middleWeight[0] || 0);
  return Number(middleWeight || 0);
}

function getCategoryOptions(categories: Map<number, ICategory>) {
  return Array.from(categories.values()).map((category) => ({
    label: category.name,
    value: category.id,
    info: {
      ...category,
      middle_weight: getCategoryMiddleWeight(category),
    },
  }));
}

function getDefaultCategoryQuantity(category: any) {
  if (category?.quantity !== undefined) return category.quantity;
  if (category?.info !== undefined && typeof category.info !== "object") return category.info;
  if (category?.info?.quantity !== undefined) return category.info.quantity;
  return 0;
}

function buildBatchFormValue(
  defaultValue: any,
  farms: OptionV2[] = [],
  areas: OptionV2[] = [],
  microAreas: OptionV2[] = [],
  categories: Map<number, ICategory>
): BatchFormValueV2 {
  const farm = getOptionByValue(farms, defaultValue?.farm_id);
  const area = getOptionByValue(areas, defaultValue?.area_id);
  const currentMicroArea =
    toOption(defaultValue?.current_micro_area_id) ||
    toOption(defaultValue?.current_micro_area) ||
    toOption(defaultValue?.micro_area_route_ids?.[0]) ||
    getOptionByValue(microAreas, defaultValue?.current_micro_area_id);
  const batchCategories = (defaultValue?.category || defaultValue?.categories || []).map((item: any) => {
    const categoryId = Number(item?.value || item?.id || item?.category_id);
    const category = categories.get(categoryId);
    return {
      id: categoryId,
      name: category?.name || item?.label,
      month: item?.month || category?.month || new Date().getMonth() + 1,
      quantity: getDefaultCategoryQuantity(item),
      middle_weight: getCategoryMiddleWeight(category) || item?.middle_weight || item?.weight?.value || 0,
      unit: category?.unit || item?.unit,
      age_description: category?.age_description || item?.age_description,
    };
  });

  return {
    id: defaultValue?.id,
    farm,
    area,
    initials: defaultValue?.initials || "",
    categories: batchCategories,
    total_ua: calculateTotalUa(batchCategories),
    current_micro_area: currentMicroArea,
    micro_area_route_ids: currentMicroArea ? [currentMicroArea.value] : [],
  };
}

export function CreateBatch() {
  const { setOptionForm, defaultValue } = useEditOnboarding();
  const isNew = !defaultValue;
  const flytoBoundsRef = useRef<FlyToBoundsRefProps>();
  const [categories, setCategories] = useState<Map<number, ICategory>>(new Map());
  const [microAreaAvailable, setMicroAreaAvailable] = useState<OptionV2[]>([]);
  const [areaCoords, setAreaCoords] = useState<ICoords>([]);
  const [listFarms, setListFarms] = useState<OptionV2[]>([]);
  const [listAreas, setListAreas] = useState<OptionV2[]>([]);
  const [currentFarm, setCurrentFarm] = useState<any>();
  const [currentArea, setCurrentArea] = useState<any>();
  const [currentInitials, setCurrentInitials] = useState("");
  const [msgError, setMsgError] = useState("");
  const [lengthBatch, setLengthBatch] = useState(0);
  const [microAreaMapCoords, setMicroAreaMapCoords] = useState<ICoords>([]);
  const [submiting, setSubmiting] = useState(false);
  const [batchV2, setBatchV2] = useState<BatchFormValueV2>(() => createEmptyBatchForm());
  const [errorsV2, setErrorsV2] = useState<FieldErrorsV2<BatchFormField>>({});

  const contentStyleAlert = {
    width: "335px",
    height: "237px",
    borderRadius: "20px",
    padding: "20px",
  } as CSSProperties;

  const categoryOptions = useMemo(() => getCategoryOptions(categories), [categories]);
  const filteredAreas = useMemo(
    () =>
      (listAreas || [])
        .filter((area: any) => !batchV2.farm || String(area?.info?.farm?.id) === String(batchV2.farm?.value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [batchV2.farm, listAreas]
  );
  const animalsQuantity = batchV2.categories.reduce((sum, category) => sum + Number(category.quantity || 0), 0);

  function validateBatch(value = batchV2) {
    const validation: FieldErrorsV2<BatchFormField> = {};

    if (!value.farm) validation.farm = "Fazenda é necessária.";
    if (!value.area) validation.area = "Retiro é necessário.";
    if (!value.initials?.trim()) validation.initials = "Sigla é necessário.";
    if (value.initials && /\s/.test(value.initials)) validation.initials = "Esta sigla não é válida.";
    if (!value.categories.length) validation.categories = "Categoria é necessária.";
    value.categories.forEach((category) => {
      if (Number(category.quantity || 0) < 1) {
        validation[`categories.${category.id}.quantity` as BatchFormField] = "Adicione animais para essa categoria.";
      }
    });
    if (!value.current_micro_area) validation.current_micro_area = "Pasto atual é necessário.";

    setErrorsV2(validation);
    return Object.keys(validation).length === 0;
  }

  function submitBatchV2() {
    if (submiting || !isNew) return;
    if (!validateBatch()) return;

    setSubmiting(true);
    const formCreateBatch = {
      initials: batchV2.initials,
      categories: batchV2.categories.map((category) => ({
        id: category.id,
        month: category.month,
        quantity: Number(category.quantity),
      })),
      area_id: batchV2.area?.value,
      current_micro_area_id: batchV2.current_micro_area?.value,
      micro_area_route_ids: batchV2.current_micro_area ? [batchV2.current_micro_area.value] : [],
    };

    axios
      .post("/batches", [formCreateBatch])
      .then(() => {
        setOptionForm(undefined);
      })
      .catch((err) => setMsgError(err.response?.data?.msg || "Erro ao criar lote"))
      .finally(() => {
        setSubmiting(false);
      });
  }

  function deleteBatch() {
    axios
      .delete(`/batches/${defaultValue.id}`)
      .then(() => {
        setOptionForm(undefined);
      })
      .catch((err) => setMsgError(err.response?.data?.msg || "Erro ao excluir lote"));
  }

  function handleFarmChange(farm: OptionV2 | null) {
    setCurrentFarm(farm?.info);
    setCurrentArea(undefined);
    setMicroAreaMapCoords([]);
    setAreaCoords(normalizeCoords(farm?.info?.map_coords));
  }

  function handleAreaChange(area: OptionV2 | null) {
    setCurrentArea(area?.info);
    setMicroAreaMapCoords([]);
    setAreaCoords(normalizeCoords(area?.info?.map_coords));
  }

  function handleCurrentMicroAreaChange(microArea: OptionV2 | null) {
    setMicroAreaMapCoords(normalizeCoords(microArea?.info?.map_coords));
  }

  useEffect(() => {
    axios.get("/farms/user").then(({ data }) => {
      setListFarms(
        data.map((farm: any) => ({
          label: farm.name,
          value: farm.id,
          info: farm,
        }))
      );
    });

    axios.get("/areas/user").then(({ data }) => {
      setListAreas(
        data.map((area: any) => ({
          label: area.name,
          value: area.id,
          info: area,
        }))
      );
    });

    if (!isNew) {
      axios
        .get("/batches", {
          params: { farm_id: defaultValue?.farm_id, area_id: defaultValue?.area_id },
        })
        .then(({ data }) => {
          if (data && data.length > 0) {
            setAreaCoords(normalizeCoords(data[0]?.micro_area_route?.[0]?.map_coords));
          }
        });
    }
  }, [defaultValue, isNew]);

  useEffect(() => {
    if (!currentFarm) {
      setCategories(new Map());
      return;
    }

    axios.get(`/evolutions/${currentFarm.id}`).then(({ data }) => {
      const currentMonth = new Date().getMonth() + 1;
      const values = data.map((type: any) =>
        type.categories.map((category: any) => ({
          ...category,
          month: currentMonth,
          unit: category.month_evolution[currentMonth - 1].weight.unit,
          middle_weight: category.month_evolution[currentMonth - 1].weight.value,
        }))
      );
      setCategories(new Map(values.flat().map((value: any) => [value.id, value])));
    });
  }, [currentFarm]);

  useEffect(() => {
    const areaId = currentArea?.id || defaultValue?.area_id;
    if (!areaId) return;

    axios
      .get("/micro-areas/available", {
        params: { area_id: areaId },
      })
      .then(({ data }) => {
        const available: OptionV2[] = data.map((microArea: any) => ({
          label: `${microArea.farm.initials}-${microArea.area.initials}-${microArea.initials}`,
          value: microArea.id,
          info: microArea,
        }));
        if (defaultValue) {
          defaultValue?.micro_area_route_ids?.forEach((value: any) => {
            if (!available.some((option) => String(option.value) === String(value.value))) available.push(value);
          });
        }
        setMicroAreaAvailable(available);
      });
  }, [currentArea, defaultValue]);

  useEffect(() => {
    if (currentFarm) {
      setCurrentInitials(`${currentFarm?.initials}-`);
      axios
        .get("/batches", {
          params: { farm_id: currentFarm?.id },
        })
        .then(({ data }) => {
          setLengthBatch(data.length);
        });
    }
  }, [currentFarm]);

  useEffect(() => {
    if (currentArea) {
      setCurrentInitials(`${currentFarm?.initials}-${currentArea?.initials}-`);
    }
  }, [currentArea, currentFarm]);

  useEffect(() => {
    if (!defaultValue || !listFarms.length) return;
    const farm = getOptionByValue(listFarms, defaultValue.farm_id);
    if (farm) setCurrentFarm(farm.info);
  }, [defaultValue, listFarms]);

  useEffect(() => {
    if (!defaultValue || !listAreas.length) return;
    const area = getOptionByValue(listAreas, defaultValue.area_id);
    if (area) {
      setCurrentArea(area.info);
      setAreaCoords(normalizeCoords(area.info?.map_coords));
    }
  }, [defaultValue, listAreas]);

  useEffect(() => {
    if (!defaultValue || !microAreaAvailable.length || !categories.size) return;
    const nextValue = buildBatchFormValue(defaultValue, listFarms, listAreas, microAreaAvailable, categories);
    setBatchV2(nextValue);
  }, [categories, defaultValue, listAreas, listFarms, microAreaAvailable]);

  useEffect(() => {
    const currentMicroArea = batchV2.current_micro_area;
    if (!currentMicroArea && !defaultValue?.micro_area_route_ids?.length) return;

    const optionCoords = normalizeCoords(currentMicroArea?.info?.map_coords);
    if (optionCoords.length) {
      setMicroAreaMapCoords(optionCoords);
      return;
    }

    const microAreaId = currentMicroArea?.value || defaultValue?.micro_area_route_ids?.[0]?.value;
    if (!microAreaId) return;

    axios.get(`/micro-areas/${microAreaId}`).then(({ data }) => {
      setMicroAreaMapCoords(
        data?.micro_area_map_coords?.map((coords: any) => ({
          lat: coords?.latitude,
          lng: coords?.longitude,
        })) || []
      );
    });
  }, [batchV2.current_micro_area, defaultValue]);

  const sideSlot = (
    <div className="edit-batch-v2__side-slot">
      <div className="area__drawarea--container edit-onboarding-v2__map-preview mapBatches">
        <LeafletStaticMap>
          <FlyToBounds ref={flytoBoundsRef} fitCoords={microAreaMapCoords.length ? microAreaMapCoords : areaCoords}>
            <div className="FlytoBounds_container">
              <div className="subtitle__animals">
                <Animais fill="#4D4D4D" />
                <span>{animalsQuantity}</span>
              </div>
            </div>
            {areaCoords.length > 0 && <Polygon positions={areaCoords} color={Constants.LEAFLET_UNFOCUS.color} />}
            {microAreaMapCoords.length > 0 && (
              <Polygon
                positions={microAreaMapCoords}
                color={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.color}
                fillColor={Constants.LEAFLET_NORMAL_BLUE.fillColor}
                fillOpacity={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.fillOpacity}
              />
            )}
          </FlyToBounds>
        </LeafletStaticMap>
      </div>
      <div className="edit-batch-v2__ua-total">
        <span>UA total</span>
        <strong>{batchV2.total_ua || "0.00"} UA</strong>
      </div>
      <div className="edit-batch-v2__animals-total">
        <span>Total de animais</span>
        <strong>
          {animalsQuantity} {animalsQuantity === 1 ? "animal" : "animais"}
        </strong>
      </div>
    </div>
  );

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError("")} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>Algo deu errado.</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError("")}>Voltar</ButtonRedirect>
        </section>
      </AlertModal>

      <main className={`body__formEditOnboarding${isNew ? "" : "--onlyRead "} body__formEditOnboarding--batch-v2`}>
        <BatchFormV2
          value={batchV2}
          mode={isNew ? "create" : "readonly"}
          farms={listFarms.sort((a, b) => a.label.localeCompare(b.label))}
          areas={filteredAreas}
          microAreas={microAreaAvailable.sort((a, b) => a.label.localeCompare(b.label))}
          categories={categoryOptions}
          initialsPrefix={isNew ? currentInitials : `${defaultValue?.initialsArea || ""}-`}
          autoGenerateInitials={isNew}
          batchIndex={lengthBatch}
          showTotalUaField={false}
          errors={errorsV2}
          showActions={false}
          showCardHeader={false}
          sideSlot={sideSlot}
          onFarmChange={handleFarmChange}
          onAreaChange={handleAreaChange}
          onCurrentMicroAreaChange={handleCurrentMicroAreaChange}
          onChange={(nextValue) => {
            setBatchV2(nextValue);
            if (Object.keys(errorsV2).length) validateBatch(nextValue);
          }}
        />
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submitBatchV2}
        deleteFunction={isNew ? undefined : deleteBatch}
        isNew={isNew}
        onlyRead
        disabledSubmit={categories.size === 0 || submiting}
      />
    </>
  );
}
